import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { cn } from "@/lib/utils";
import { FlameIcon, HeartIcon, RocketIcon } from "lucide-react";
import { Button } from "@somethings-ui/web";
import { useEffect, useState } from "react";
import { DeepThoughtsPrerequisitesStatus } from "@/lib/api/activities.ts";

type Card = {
  image: string;
  rotate: number;
  left: number;
  top: number;
  width: number;
  height: number;
  caption: string;
  description: string;
  pillLabel: string;
};

const CARDS: Card[] = [
  {
    image: "/assets/deep-thoughts/1.png",
    rotate: -10,
    left: 166,
    top: 6,
    width: 130,
    height: 280,
    caption: "CHAT NOTIFICATIONS",
    description:
      "The teen receives this unlock card in their chat when Deep Thoughts gets sent to them",
    pillLabel: "In chat message",
  },
  {
    image: "/assets/deep-thoughts/2.png",
    rotate: -5,
    left: 246,
    top: -5,
    width: 128,
    height: 277,
    caption: "PROMPT 1",
    description:
      "A lightweight reflection check-in that helps your teen pick a topic to dig into",
    pillLabel: "What's on your mind",
  },
  {
    image: "/assets/deep-thoughts/3.png",
    rotate: 5,
    left: 330,
    top: -5,
    width: 130,
    height: 280,
    caption: "PROMPT 2",
    description:
      "Asks what feels heaviest and why, encouraging detail behind the chosen topic",
    pillLabel: "What feels heaviest",
  },
  {
    image: "/assets/deep-thoughts/4.png",
    rotate: 10,
    left: 404,
    top: 6,
    width: 130,
    height: 280,
    caption: "VOICE RESPONSE",
    description:
      "Captures a voice note for moments that are too hard to put into typed words",
    pillLabel: "Voice response",
  },
];

const FAN_WIDTH = 700;
const FAN_HEIGHT = 420;
const FAN_OFFSET_Y = 50;
const TOOLTIP_WIDTH = 246;
const TOOLTIP_GAP = 16;
const HOVER_SCALE = 1.8;
const HOVER_LIFT = 28;

function getTooltipPosition(card: Card) {
  const centerX = card.left + card.width / 2;
  const grownHalfWidth = (card.width * HOVER_SCALE) / 2;
  const grownHalfHeight = (card.height * HOVER_SCALE) / 2;
  const grownTop =
    card.top + FAN_OFFSET_Y + card.height / 2 - grownHalfHeight - HOVER_LIFT;
  const grownRight = centerX + grownHalfWidth;
  const grownLeft = centerX - grownHalfWidth;

  const rightFits = grownRight + TOOLTIP_GAP + TOOLTIP_WIDTH <= FAN_WIDTH;
  const left = rightFits
    ? grownRight + TOOLTIP_GAP
    : Math.max(0, grownLeft - TOOLTIP_GAP - TOOLTIP_WIDTH);

  return { left, top: Math.max(grownTop, 0) };
}

type Step = "prereqs" | "intro";

interface DeepThoughtsIntroProps {
  prereqs: DeepThoughtsPrerequisitesStatus | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  teenName: string;
  onSend?: () => void;
  isSending?: boolean;
  onOpenHotTakes?: () => void;
  onOpenConnectionCards?: () => void;
}

export function DeepThoughtsIntro({
  isOpen,
  onOpenChange,
  teenName,
  onSend,
  isSending = false,
  prereqs,
  onOpenHotTakes,
  onOpenConnectionCards,
}: DeepThoughtsIntroProps) {
  const requiresPrereqs = !!prereqs && !prereqs.allMet;
  const [step, setStep] = useState<Step>(requiresPrereqs ? "prereqs" : "intro");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const activeCard = activeIndex !== null ? CARDS[activeIndex] : null;
  const tooltipPos = activeCard ? getTooltipPosition(activeCard) : null;

  useEffect(() => {
    if (isOpen) {
      setStep(requiresPrereqs ? "prereqs" : "intro");
      setActiveIndex(null);
    }
  }, [isOpen, requiresPrereqs]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[800px] gap-0 rounded-2xl p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex flex-col items-center gap-2 border-b border-surfaces-divider p-8 text-center">
          <DialogTitle className="font-avantt flex items-center justify-center gap-2 text-2xl font-bold leading-[1.15] tracking-[-0.24px] text-text-primary">
            <HeartIcon className="h-6 w-6" strokeWidth={1.75} />
            Deep thoughts
          </DialogTitle>
          <p className="font-twk text-sm leading-[1.4] text-secondary">
            Help your teen open up about what&apos;s going on
          </p>
        </DialogHeader>

        {step === "prereqs" ? (
          <div className="flex flex-col items-center justify-center gap-4 px-6 py-6 text-center">
            <p className="font-twk max-w-[480px] text-base leading-[1.4] text-black">
              We&apos;d recommend sending Hot Takes and Connection cards first —
              but you know your mentorship best.
            </p>
            <div className="flex items-start gap-8">
              <button
                type="button"
                onClick={onOpenHotTakes}
                className="font-twk flex h-12 items-center gap-2 rounded-xl bg-surfaces-z3 px-4 py-3 text-base leading-6 text-secondary transition-colors hover:bg-surfaces-z4"
              >
                <FlameIcon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                Hot takes
              </button>
              <button
                type="button"
                onClick={onOpenConnectionCards}
                className="font-twk flex h-12 items-center gap-2 rounded-xl bg-surfaces-z3 px-4 py-3 text-base leading-6 text-secondary transition-colors hover:bg-surfaces-z4"
              >
                <RocketIcon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                Connection cards
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-6">
            <div
              className="relative mx-auto"
              style={{ width: FAN_WIDTH, height: FAN_HEIGHT }}
            >
              {CARDS.map((card, index) => {
                const isActive = activeIndex === index;
                const hasHover = activeIndex !== null;

                return (
                  <button
                    key={card.image}
                    type="button"
                    tabIndex={-1}
                    className="absolute origin-center cursor-pointer outline-none transition-all duration-300 ease-out"
                    style={{
                      left: card.left,
                      top: card.top + FAN_OFFSET_Y,
                      width: card.width,
                      height: card.height,
                      zIndex: isActive ? 50 : index + 1,
                      transform: isActive
                        ? `translateY(${-HOVER_LIFT}px) scale(${HOVER_SCALE}) rotate(0deg)`
                        : `rotate(${card.rotate}deg)`,
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                    aria-label={`Preview ${card.pillLabel}`}
                  >
                    <div className="relative h-full w-full overflow-hidden rounded-lg border border-surfaces-divider shadow-[0_0_16px_rgba(0,0,0,0.08)]">
                      <img
                        src={card.image}
                        alt={card.caption}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <div
                        className={cn(
                          "absolute inset-0 bg-[rgba(142,142,142,0.2)] transition-opacity duration-200",
                          hasHover && !isActive ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </div>
                  </button>
                );
              })}

              {activeCard && tooltipPos && (
                <div
                  className="pointer-events-none absolute z-[60] flex flex-col gap-4 rounded-2xl bg-white p-4 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]"
                  style={{
                    width: TOOLTIP_WIDTH,
                    left: tooltipPos.left,
                    top: tooltipPos.top,
                  }}
                >
                  <p className="font-mono text-xs font-medium uppercase leading-[1.4] tracking-[-0.24px] text-brand-tangelo">
                    {activeCard.caption}
                  </p>
                  <p className="font-avantt text-base font-bold leading-[1.2] tracking-[-0.16px] text-text-primary">
                    {activeCard.description}
                  </p>
                  <div className="self-start rounded-full bg-surfaces-z4 px-4 py-1">
                    <p className="font-twk text-xs leading-[1.4] text-secondary">
                      {activeCard.pillLabel}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <p className="font-twk text-sm leading-[1.4] text-secondary">
              Hover over image to preview
            </p>
          </div>
        )}

        <DialogFooter
          className={cn(
            "flex items-center border-t border-surfaces-divider p-4",
            step === "prereqs"
              ? "sm:justify-end"
              : "justify-center sm:justify-center",
          )}
        >
          {step === "prereqs" ? (
            <Button
              shape="pill"
              size="md"
              fullWidth={false}
              onClick={() => setStep("intro")}
            >
              Continue anyway
            </Button>
          ) : (
            <Button
              shape="pill"
              size="md"
              fullWidth={false}
              onClick={onSend}
              loading={isSending}
            >
              Send to {teenName}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
